import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, List, BarChart3, Radio, Wallet, Trash2, Copy, Check, ExternalLink, QrCode } from 'lucide-react';
import { copyText } from '../utils/clipboard.js';
import Layout from '../components/Layout.js';
import QrWallpaperDialog from '../components/QrWallpaperDialog.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

interface Card {
  id?: string;
  slug: string;
  shortCode: string | null;
  orgSlug: string | null;
  name: string;
  title: string;
  avatar: string | null;
  email: string;
  userId?: string;
  userEmail?: string;
}

export default function Cards() {
  const { t, i18n } = useAppTranslation();
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [liveCounts, setLiveCounts] = useState<Record<string, number>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [qrCard, setQrCard] = useState<Card | null>(null);

  const cardUrl = (shortCode: string) => `${window.location.origin}/card/${shortCode}`;
  async function handleCopy(key: string, url: string) {
    if (await copyText(url)) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
    }
  }

  useEffect(() => {
    fetch('/api/admin/cards', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        setCards(Array.isArray(data) ? data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Poll live-viewer counts every 30s (only if we have cards with ids)
  useEffect(() => {
    const ids = cards.map(c => c.id).filter((x): x is string => Boolean(x));
    if (ids.length === 0) return;
    let active = true;
    const tick = async () => {
      try {
        const r = await fetch('/api/analytics/live/batch', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cardIds: ids }),
        });
        if (active && r.ok) {
          const data = await r.json() as Record<string, number>;
          setLiveCounts(data);
        }
      } catch { /* ignore */ }
    };
    void tick();
    const h = setInterval(tick, 30_000);
    return () => { active = false; clearInterval(h); };
  }, [cards]);

  async function handleDelete(slug: string, userId?: string) {
    if (!confirm(t('cards.confirmDelete'))) return;
    const res = await fetch(`/api/cards/${slug}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ userId }),
    });
    if (res.ok) {
      setCards((prev) => prev.filter((c) => c.slug !== slug));
    }
  }

  function handleWallet(slug: string, userId?: string) {
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    window.open(`/api/cards/${slug}/wallet${query}`, '_blank');
  }

  // users without a card come back as rows without a slug; they are not cards
  const filtered = cards.filter((c) => c.slug).filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.shortCode ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout title={t('cards.pageTitle', 'Business Cards')}>
      <div className="max-w-6xl space-y-5">
        {/* Toolbar */}
        <div className="flex items-center gap-4 flex-wrap">
          <input
            type="search"
            placeholder={t('cards.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] sm:max-w-xs rounded-lg bg-panel border border-line px-4 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition"
          />

          {/* View toggle */}
          <div className="flex items-center gap-1 rounded-lg bg-panel border border-line p-1">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              title={t('cards.view.grid')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'grid'
                  ? 'bg-accent-600 text-white shadow-sm'
                  : 'text-ink-faint hover:text-ink hover:bg-raised'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              title={t('cards.view.list')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'list'
                  ? 'bg-accent-600 text-white shadow-sm'
                  : 'text-ink-faint hover:text-ink hover:bg-raised'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          <Link
            to="/cards/new"
            className="rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            + {t('cards.newCard')}
          </Link>
        </div>

        {/* Cards — loading skeleton */}
        {loading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="rounded-xl bg-panel border border-line h-44 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="rounded-xl bg-panel border border-line h-14 animate-pulse" />
              ))}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="rounded-xl bg-panel border border-line p-12 text-center text-ink-faint">
            {search ? t('cards.noResults') : t('cards.empty')}
          </div>
        ) : viewMode === 'grid' ? (
          /* ── Grid view ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {filtered.map((card) => (
              <div
                key={`${card.slug}-${card.userId ?? ''}`}
                className="group overflow-hidden rounded-xl bg-panel border border-line hover:border-line-strong transition-colors"
              >
                {/* Card thumbnail: reads as a business card, not a generic tile */}
                <div className="relative flex aspect-[2.6/1] flex-col justify-between bg-gradient-to-br from-accent-600 to-accent-900 p-4 text-white">
                  <div className="flex items-start justify-between">
                    {card.avatar ? (
                      <img src={card.avatar} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-white/30" />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-base font-semibold ring-2 ring-white/20" aria-hidden="true">
                        {card.name ? card.name.charAt(0).toUpperCase() : '?'}
                      </div>
                    )}
                    {card.id && liveCounts[card.id] ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success-100 dark:bg-success-400/20 px-2 py-0.5 text-[11px] font-medium text-success-100">
                        <span className="h-1.5 w-1.5 rounded-full bg-success-300 animate-pulse" />
                        <Radio className="h-3 w-3" aria-hidden="true" />
                        {liveCounts[card.id]}
                      </span>
                    ) : null}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold leading-tight">{card.name || t('cards.unnamed')}</p>
                    <p className="truncate text-xs text-white/75">{card.title || card.email}</p>
                  </div>
                </div>

                <div className="space-y-2.5 p-3">
                  {card.shortCode && (
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-ink-faint">{t('cards.qrLabel')}</span>
                      <span className="font-mono font-medium text-ink">{card.shortCode}</span>
                      <button
                        type="button"
                        onClick={() => handleCopy(`${card.slug}-${card.userId ?? ''}`, cardUrl(card.shortCode!))}
                        title={t('cards.copyUrl', 'Link kopieren')}
                        aria-label={t('cards.copyUrl', 'Link kopieren')}
                        className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-ink-muted hover:bg-raised hover:text-ink transition-colors"
                      >
                        {copiedKey === `${card.slug}-${card.userId ?? ''}`
                          ? <><Check className="h-3.5 w-3.5 text-success-500" /> {t('cards.copied', 'Kopiert')}</>
                          : <><Copy className="h-3.5 w-3.5" /> {t('cards.copy')}</>}
                      </button>
                      <a
                        href={cardUrl(card.shortCode)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('cards.openCard', 'Karte öffnen')}
                        aria-label={t('cards.openCard', 'Karte öffnen')}
                        className="rounded-md p-1.5 text-ink-muted hover:bg-raised hover:text-ink transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Link
                      to={`/cards/${card.slug}${card.userId ? `?userId=${card.userId}` : ''}`}
                      className="flex-1 whitespace-nowrap rounded-lg bg-raised px-3 py-2 text-center text-sm font-medium text-ink hover:bg-line transition-colors"
                    >
                      {t('cards.editCard')}
                    </Link>
                    <Link
                      to={`/analytics?card=${card.id ?? ''}${card.userId ? `&userId=${card.userId}` : ''}`}
                      onClick={(e) => { if (!card.id) e.preventDefault(); }}
                      title={t('cards.analytics', 'Analytics')}
                      aria-label={t('cards.analytics', 'Analytics')}
                      className="rounded-lg border border-line p-2 text-ink-muted hover:bg-raised hover:text-ink transition-colors"
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Link>
                    {card.shortCode && (
                      <button
                        onClick={() => setQrCard(card)}
                        title={t('cards.qrWallpaper', 'QR-Hintergrund & QR-Code')}
                        aria-label={t('cards.qrWallpaper', 'QR-Hintergrund & QR-Code')}
                        className="rounded-lg border border-line p-2 text-ink-muted hover:bg-raised hover:text-ink transition-colors"
                      >
                        <QrCode className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleWallet(card.slug, card.userId)}
                      title={t('cards.wallet')}
                      aria-label={t('cards.wallet')}
                      className="rounded-lg border border-line p-2 text-ink-muted hover:bg-raised hover:text-ink transition-colors"
                    >
                      <Wallet className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(card.slug, card.userId)}
                      title={t('cards.deleteCard')}
                      aria-label={t('cards.deleteCard')}
                      className="rounded-lg border border-line p-2 text-ink-faint hover:border-danger-500/40 hover:bg-danger-500/10 hover:text-danger-500 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ── List view ── */
          <div className="rounded-xl bg-panel border border-line overflow-hidden">
            {filtered.map((card, idx) => (
              <div
                key={`${card.slug}-${card.userId ?? ''}`}
                className={`flex items-center gap-4 px-5 py-3 hover:bg-slate-200/50 dark:hover:bg-white/[0.03] transition-all ${
                  idx !== 0 ? 'border-t border-slate-200 dark:border-white/[0.06]' : ''
                }`}
              >
                {/* Avatar */}
                <div className="h-9 w-9 rounded-full bg-accent-600/40 flex items-center justify-center text-sm font-semibold text-accent-700 dark:text-accent-300 flex-shrink-0 ring-1 ring-white/10">
                  {card.name ? card.name.charAt(0).toUpperCase() : '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-0.5 sm:gap-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-semibold text-sm text-ink truncate">{card.name || t('cards.unnamed')}</p>
                    {card.id && liveCounts[card.id] ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-success-500/30 bg-success-50 dark:bg-success-500/10 px-1.5 py-0.5 text-[10px] font-medium text-success-700 dark:text-success-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-success-500 animate-pulse" />
                        <Radio className="h-2.5 w-2.5" />
                        {liveCounts[card.id]}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-ink-muted truncate">{card.email}</p>
                  <p className="text-xs font-mono text-ink-faint truncate">{card.slug}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to={`/cards/${card.slug}${card.userId ? `?userId=${card.userId}` : ''}`}
                    className="rounded-lg bg-panel border border-line px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-raised hover:text-ink transition-all whitespace-nowrap"
                  >
                    {t('cards.editCard')}
                  </Link>
                  <Link
                    to={`/analytics?card=${card.id ?? ''}${card.userId ? `&userId=${card.userId}` : ''}`}
                    onClick={(e) => { if (!card.id) e.preventDefault(); }}
                    title={t('cards.analytics', 'Analytics')}
                    className="rounded-lg bg-panel border border-line px-2.5 py-1.5 text-xs text-ink-muted hover:bg-raised hover:text-ink transition-all"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Link>
                  {card.shortCode && (
                    <button
                      onClick={() => setQrCard(card)}
                      title={t('cards.qrWallpaper', 'QR-Hintergrund & QR-Code')}
                      aria-label={t('cards.qrWallpaper', 'QR-Hintergrund & QR-Code')}
                      className="rounded-lg bg-panel border border-line px-2.5 py-1.5 text-xs text-ink-muted hover:bg-raised hover:text-ink transition-all"
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleWallet(card.slug, card.userId)}
                    title={t('cards.walletDownload')}
                    className="rounded-lg bg-panel border border-line px-2.5 py-1.5 text-xs text-ink-muted hover:bg-raised hover:text-ink transition-all"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                      <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 3c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm4 10H8v-1c0-2 2.67-3 4-3s4 1 4 3v1z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(card.slug, card.userId)}
                    className="rounded-lg bg-panel border border-line px-3 py-1.5 text-xs text-danger-400/70 hover:bg-danger-500/10 hover:text-danger-400 hover:border-danger-500/20 transition-all"
                  >
                    {t('cards.deleteCard')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {qrCard?.shortCode && (
        <QrWallpaperDialog url={`${window.location.origin}/${qrCard.shortCode}`} name={qrCard.name || qrCard.slug} onClose={() => setQrCard(null)} />
      )}
    </Layout>
  );
}
